import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import {
  fetchPackages,
  createPackage,
  updatePackage,
  deletePackage,
} from "../redux/packageSlice";
import "./css/AdminSessionsPage.css";

const AdminPackagesPage = () => {
  const dispatch = useDispatch();
  const { items: packages, loading } = useSelector((state) => state.packages);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);

  const emptyForm = {
    name: "",
    description: "",
    price: "",
    sessionCount: "",
    color: "#2563EB",
    isActive: true,
    showOnHomepage: true,
    order: 0,
  };

  const [formData, setFormData] = useState(emptyForm);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    dispatch(fetchPackages({}));
  }, [dispatch]);

  useEffect(() => {
    if (openDialog || confirmDelete) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [openDialog, confirmDelete]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        description: pkg.description || "",
        price: pkg.price,
        sessionCount: pkg.sessionCount,
        color: pkg.color || "#2563EB",
        isActive: pkg.isActive,
        showOnHomepage: pkg.showOnHomepage,
        order: pkg.order || 0,
      });
    } else {
      setEditingPackage(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPackage(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.price || !formData.sessionCount) {
      showToast("İsim, fiyat ve seans sayısı zorunludur", "error");
      return;
    }

    const data = {
      ...formData,
      price: parseFloat(formData.price),
      sessionCount: parseInt(formData.sessionCount),
      order: parseInt(formData.order) || 0,
    };

    try {
      if (editingPackage) {
        await dispatch(updatePackage({ id: editingPackage._id, data })).unwrap();
        showToast("Paket güncellendi");
      } else {
        await dispatch(createPackage(data)).unwrap();
        showToast("Paket oluşturuldu");
      }
      handleCloseDialog();
      dispatch(fetchPackages({}));
    } catch (error) {
      showToast(error || "İşlem başarısız", "error");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deletePackage(packageToDelete)).unwrap();
      showToast("Paket silindi", "info");
      dispatch(fetchPackages({}));
    } catch {
      showToast("Silme başarısız", "error");
    } finally {
      setConfirmDelete(false);
      setPackageToDelete(null);
    }
  };

  return (
    <div className="admin-sessions-page">
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Paket Yönetimi</h1>
          <button className="btn btn-primary" onClick={() => handleOpenDialog()}>
            <span className="btn-icon">➕</span>
            Yeni Paket
          </button>
        </div>

        <div className="table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Paket Adı</th>
                <th>Açıklama</th>
                <th>Seans Sayısı</th>
                <th>Fiyat</th>
                <th>Durum</th>
                <th>Anasayfa</th>
                <th className="actions-cell">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((pkg) => (
                <tr key={pkg._id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          backgroundColor: pkg.color || "#2563EB",
                          flexShrink: 0,
                        }}
                      />
                      <strong>{pkg.name}</strong>
                    </div>
                  </td>
                  <td style={{ maxWidth: 220, fontSize: "0.9em", color: "#555" }}>
                    {pkg.description || "-"}
                  </td>
                  <td>{pkg.sessionCount} seans</td>
                  <td className="price-cell">{pkg.price.toLocaleString("tr-TR")} TL</td>
                  <td>
                    <span className={`status-badge ${pkg.isActive ? "status-success" : "status-error"}`}>
                      {pkg.isActive ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${pkg.showOnHomepage ? "status-primary" : "status-default"}`}>
                      {pkg.showOnHomepage ? "Gösteriliyor" : "Gizli"}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button
                      className="icon-btn icon-btn-edit"
                      onClick={() => handleOpenDialog(pkg)}
                      title="Düzenle"
                    >
                      ✏️
                    </button>
                    <button
                      className="icon-btn icon-btn-delete"
                      onClick={() => { setPackageToDelete(pkg._id); setConfirmDelete(true); }}
                      title="Sil"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {packages.length === 0 && !loading && (
            <div className="empty-state"><p>Henüz paket tanımlanmadı</p></div>
          )}
        </div>
      </div>

      {/* Oluştur/Düzenle Modal */}
      {openDialog && (
        <div className="modal-overlay" onClick={handleCloseDialog}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPackage ? "Paket Düzenle" : "Yeni Paket"}</h2>
              <button className="modal-close" onClick={handleCloseDialog}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Paket Adı <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="ör. 10 Seanslık Başlangıç Paketi"
                  />
                </div>

                <div className="form-group">
                  <label>Fiyat (TL) <span className="required">*</span></label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Seans Sayısı <span className="required">*</span></label>
                  <input
                    type="number"
                    min="1"
                    value={formData.sessionCount}
                    onChange={(e) => setFormData({ ...formData, sessionCount: e.target.value })}
                    placeholder="10"
                  />
                </div>

                <div className="form-group">
                  <label>Renk (kart rengi)</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      style={{ width: 48, height: 36, padding: 2, border: "1px solid #ddd", borderRadius: 6, cursor: "pointer" }}
                    />
                    <span style={{ fontSize: "0.9em", color: "#666" }}>{formData.color}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Sıralama (anasayfada)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Durum</label>
                  <select
                    value={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === "true" })}
                  >
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Anasayfada Göster</label>
                  <select
                    value={formData.showOnHomepage}
                    onChange={(e) => setFormData({ ...formData, showOnHomepage: e.target.value === "true" })}
                  >
                    <option value="true">Evet</option>
                    <option value="false">Hayır</option>
                  </select>
                </div>

                <div className="form-group form-group-full">
                  <label>Açıklama</label>
                  <textarea
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Paket hakkında kısa açıklama..."
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseDialog}>İptal</button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!formData.name || !formData.price || !formData.sessionCount}
              >
                {editingPackage ? "Güncelle" : "Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onayı */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(false)}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Paketi Sil</h2>
              <button className="modal-close" onClick={() => setConfirmDelete(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Bu paketi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>İptal</button>
              <button className="btn btn-danger" onClick={handleDeleteConfirm}>Sil</button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={toast.show}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, show: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.type}>{toast.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default AdminPackagesPage;
